import { fetchWithAuth, getDefaultHeaders } from '../config/api';

/**
 * The people on a business account.
 *
 * SEATS ARE PEOPLE. `seats.used` counts members AND pending invitations, because an invitation
 * that has been sent already holds the seat it will occupy — the server enforces the same rule, so
 * nothing here re-derives it and risks the two disagreeing.
 */

const ENDPOINTS = {
    MEMBERS: '/api/Business/me/members',
    INVITE: '/api/Business/me/members/invite',
    MEMBER: (userId: string) => `/api/Business/me/members/${encodeURIComponent(userId)}`,
    INVITATION: (id: string) => `/api/Business/me/invitations/${encodeURIComponent(id)}`,
    ACCEPT: '/api/Business/invitations/accept',
};

export interface BusinessMember {
    id: string;
    name: string;
    email: string;
    isOwner: boolean;
    isYou: boolean;
    status: string;
}

export interface PendingInvitation {
    id: string;
    name: string;
    email: string;
    invitedAt: string;
    expiresAt: string;
}

export interface BusinessMembers {
    members: BusinessMember[];
    pendingInvitations: PendingInvitation[];
    seats: { allowance: number; used: number; canInvite: boolean };
    isOwner: boolean;
}

/**
 * What freeing a seat did to the bill.
 *
 * <p>Both `removeMember` and `revokeInvitation` stop paying for the seat they free — scheduled for
 * the end of the period already paid for, so this month is unchanged. `seatsFrom` is when the
 * smaller invoice starts; null means nothing was scheduled (the seat count was already right, the
 * plan is not seat-priced, or the catch-up could not run).</p>
 */
export type SeatChange = {
    seatsFrom: string | null;
    /** The head count the subscription was aligned to, or null when nothing changed. */
    seats: number | null;
};

export const businessMembersApi = {
    list: (): Promise<BusinessMembers> => fetchWithAuth<BusinessMembers>(ENDPOINTS.MEMBERS),

    invite: (email: string, name?: string): Promise<PendingInvitation & { emailSent: boolean }> =>
        fetchWithAuth(ENDPOINTS.INVITE, {
            method: 'POST',
            headers: getDefaultHeaders(),
            body: JSON.stringify({ email, ...(name ? { name } : {}) }),
        }),

    revokeInvitation: (invitationId: string): Promise<SeatChange> =>
        fetchWithAuth<SeatChange>(ENDPOINTS.INVITATION(invitationId), { method: 'DELETE', headers: getDefaultHeaders() }),

    removeMember: (userId: string): Promise<SeatChange> =>
        fetchWithAuth<SeatChange>(ENDPOINTS.MEMBER(userId), { method: 'DELETE', headers: getDefaultHeaders() }),

    accept: (token: string): Promise<{ businessId: string; businessName: string }> =>
        fetchWithAuth(ENDPOINTS.ACCEPT, {
            method: 'POST',
            headers: getDefaultHeaders(),
            body: JSON.stringify({ token }),
        }),
};
